import React, { useEffect, useState } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import "../FundsDeposited/fundsDeposited.scss";
import SearchBar from "../../common/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import { getTransactions } from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import debounce from "lodash.debounce"; // Ensure lodash.debounce is installed
import PrimaryButton from "../../common/PrimaryButton";
import Box from "../../common/Dashboard/Box";
import ReleaseIcon from "../../../Assets/Icons/releaseIcon.svg";
import FundsReceivedIcon from "../../../Assets/Icons/fundsReceivedIcon.svg";
import WithdrawalsDataTable from "../Withdrawals";
import FundsDepositedTable from "../FundsDeposited";
import WithdrawModal from "./WithdrawModal";

const TransactionsPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const transactions = useSelector(
    (state) => state?.fundraiserReducer?.transactions,
  );
  const [cards, setCards] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [withdrawFunds, setWithdrawFunds] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filter, setFilter] = useState("year");

  const { totalEarned, availableFunds, pendingFunds } = cards;
  const selectedType = selectedIndex === 1 ? "debit" : "credit";
  const selectedRows =
    selectedType === "debit"
      ? transactions?.debit || []
      : transactions?.credit || [];
  const totalPages = transactions?.totalPages || 1;

  const handleSelect = (index) => {
    setCurrentPage(1);
    setSelectedIndex(index);
  };

  const getTransactionsData = async () => {
    try {
      const transactionsPromise = await dispatch(
        getTransactions({
          filter,
          search: debouncedSearchQuery,
          page: currentPage,
          type: selectedType,
          silent: true,
        })
      )?.unwrap();
      if (transactionsPromise?.success === true) {
        setCards(transactionsPromise?.cards);
      } else {
        setCards({});
      }
    } catch (e) {
      // Gracefully handle empty/new accounts or network errors
      setCards({});
    }
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query) => {
        setDebouncedSearchQuery(query);
      }, 300), // 300ms debounce time
    []
  );

  const handleSearchChange = (value) => {
    setCurrentPage(1);
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    getTransactionsData();
  }, [currentPage, debouncedSearchQuery, filter, selectedType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, selectedType]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const items = [
    {
      title: "Transactions",
    },
    {
      title: "All Transactions",
    },
  ];

  const handleWithdraw = () => {
    setWithdrawFunds(true);
  };

  const fundraiserBoxes = [
    {
      text: "Total Earned",
      number: `$${totalEarned ? (totalEarned / 100).toFixed(2) : 0}`,
      icon: null,
      filter,
    },
    {
      text: "Available Funds",
      number: `$${availableFunds ? (availableFunds / 100).toFixed(2) : 0}`,
      icon: FundsReceivedIcon,
    },
    {
      text: "Pending Funds",
      number: `$${pendingFunds ? (pendingFunds / 100).toFixed(2) : 0}`,
      icon: ReleaseIcon,
    },
  ];

  const data = [
    {
      text: "Credit",
    },
    {
      text: "Debit",
    },
  ];

  return (
    <>
      {loading === true && <Loader />}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center col-span-12">
        <BreadCrumsHeader
          heading="Transactions"
          items={items}
          form={true}
        />
        <PrimaryButton
          className={
            "self-start sm:self-auto w-[60%] sm:w-[50%] md:w-[30%] lg:w-[20%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat mt-3 sm:mt-0"
          }
          label="Withdraw Funds"
          onClick={() => handleWithdraw()}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 w-full gap-3 my-[2%]">
        {fundraiserBoxes?.map((b) => (
          <Box
            text={b?.text}
            number={b?.number}
            icon={b?.icon}
            mainClassName="box p-3 h-[113px]"
            textClassName="text-[14px] font-poppins font-[600] text-[white]"
            numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
            reverse={true}
            filter={b?.filter}
            setFilter={b?.filter && setFilter}
          />
        ))}
      </div>
      <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-center gap-3 sm:gap-4">
        <SearchBar
          parentDivClass="w-full sm:w-[50%] lg:w-[33%] relative"
          divClassName="relative"
          inputClassName="searchBar md:!h-[46px] p-3 w-full text-[14px] font-poppins font-[400] outline-none"
          iconClassName="absolute right-[4%] top-1/2 -translate-y-1/2"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
          onClear={() => handleSearchChange("")}
        />
        <div className="w-full sm:w-[50%] lg:w-[67%] max-w-md flex justify-center items-center">
          <div className="w-full h-[40px] md:h-[46px] grid grid-cols-2 rounded-[6px] border border-[#00ADEF] p-1">
            {data.map((item, index) => (
              <div
                key={index}
                className={`rounded-[6px] flex justify-center items-center cursor-pointer transition-colors duration-200 ${
                  selectedIndex === index ? "bg-[#00ADEF]" : "bg-white"
                }`}
                onClick={() => handleSelect(index)}
              >
                <p
                  className={`font-sans text-center font-medium text-base ${
                    selectedIndex === index ? "text-white" : "text-[#8C8C8C]"
                  }`}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedIndex === 1 ? (
        <FundsDepositedTable
          debit={selectedRows}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      ) : (
        <WithdrawalsDataTable
          credit={selectedRows}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      )}

      <WithdrawModal
        isOpen={withdrawFunds}
        onClose={() => setWithdrawFunds(false)}
        availableFundsCents={Number(availableFunds) || 0}
        onWithdrawSuccess={() => {
          setCurrentPage(1);
          return getTransactionsData();
        }}
      />
    </>
  );
};

export default TransactionsPage;
